import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { FunctionComponent, MouseEventHandler } from 'react';
import { Fragment, useState } from 'react';

import { useSkillFeedbackModalContext } from './ModalContext';
import { useFeedbackDialogStore } from './use-feedback-dialog-store';

const FeedbackAction = styled.p({
    '&:hover': {
        backgroundColor: '#F1F1F0',
    },
    borderRadius: '0.5rem',
    color: '#6E6E6C',
    cursor: 'pointer',
    fontFamily: 'FT Regola Neue',
    fontSize: 'normal',
    fontWeight: 500,
    margin: 0,
    padding: '0.375rem 1rem',
});

const FeedbackActions: FunctionComponent<{ readonly closeActionsSelector: () => void }> = ({
    closeActionsSelector,
}) => {
    const requestNewSkillLabel = useFeedbackDialogStore((state) => state.requestNewSkillLabel);
    const submitFeedbackLabel = useFeedbackDialogStore((state) => state.submitFeedbackLabel);
    const reportSkillLabel = useFeedbackDialogStore((state) => state.reportSkillLabel);
    const reportContentIssueLabel = useFeedbackDialogStore((state) => state.reportContentIssueLabel);
    const { openFeedbackModal } = useSkillFeedbackModalContext();
    const setFeedbackType = useFeedbackDialogStore((state) => state.setFeedbackType);
    const sendFeedbackToCobrainer = useFeedbackDialogStore((state) => state.sendFeedbackToCobrainer);
    const feedbackDescriptionLabel = useFeedbackDialogStore((state) => state.feedbackDescriptionLabel);

    const sendFeedback = (): void => {
        sendFeedbackToCobrainer(feedbackDescriptionLabel);
        closeActionsSelector();
    };

    const onClickRequest: MouseEventHandler = () => {
        setFeedbackType('request');
        openFeedbackModal();
        closeActionsSelector();
    };

    const onClickReport: MouseEventHandler = () => {
        setFeedbackType('report');
        openFeedbackModal();
        closeActionsSelector();
    };

    const onClickChange: MouseEventHandler = () => {
        setFeedbackType('change');
        openFeedbackModal();
        closeActionsSelector();
    };

    return (
        <Fragment>
            <FeedbackAction onClick={sendFeedback}>{submitFeedbackLabel}</FeedbackAction>
            <FeedbackAction onClick={onClickRequest}>{requestNewSkillLabel}</FeedbackAction>
            <FeedbackAction onClick={onClickReport}>{reportSkillLabel}</FeedbackAction>
            <FeedbackAction onClick={onClickChange}>{reportContentIssueLabel}</FeedbackAction>
        </Fragment>
    );
};

const FeedbackButtonWrapper = styled.div({
    background: '#F1F1F0',
    borderRadius: '0.625rem',
    bottom: '1rem',
    boxShadow:
        '1px 1px 1px 0px #FBFBFF inset, -1px -1px 1px 0px rgba(0, 0, 0, 0.15) inset, 0.44px 0.44px 0.63px -1px rgba(0, 0, 0, 0.25), 1.21px 1.21px 1.71px -1.5px rgba(0, 0, 0, 0.25), 2.66px 2.66px 3.76px -2.25px rgba(0, 0, 0, 0.25), 5.9px 5.9px 8.35px -3px rgba(0, 0, 0, 0.19), 10px 10px 21.21px -3.75px rgba(0, 0, 0, 0.05), -0.5px -0.5px 0px 0px rgba(0, 0, 0, 0.05)',
    color: '#505052',
    cursor: 'pointer',
    fontFamily: 'FT Regola Neue',
    fontSize: '0.825rem',
    fontStyle: 'normal',
    fontWeight: 600,
    height: '2.5rem',
    lineHeight: '1.5rem',
    padding: '0.5rem 1rem',
    position: 'fixed',
    right: '1rem',
    width: '2.5rem',
    zIndex: 3,
});

export const SkillFeedbackButton: FunctionComponent = () => {
    const toggleFeedbackActionsVisibility = useFeedbackDialogStore((state) => state.toggleFeedbackActionsVisibility);
    const isFeedbackActionsVisible = useFeedbackDialogStore((state) => state.isFeedbackActionsVisible);
    const clearFeedback = useFeedbackDialogStore((state) => state.clearFeedback);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClose = (): void => {
        toggleFeedbackActionsVisibility();
        clearFeedback();
        setAnchorEl(null);
    };

    const handleClick: MouseEventHandler<HTMLElement> = (event) => {
        toggleFeedbackActionsVisibility();
        setAnchorEl(event.currentTarget as HTMLButtonElement);
    };

    return (
        <div>
            <FeedbackButtonWrapper onClick={isFeedbackActionsVisible ? handleClose : handleClick}>
                <div>?</div>
            </FeedbackButtonWrapper>
            <Popover
                anchorEl={anchorEl}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                }}
                open={isFeedbackActionsVisible}
                sx={{ transform: 'translateY(-45px)' }}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                }}
                transitionDuration={0}
                onClose={handleClose}
            >
                <FeedbackActions closeActionsSelector={handleClose} />
            </Popover>
        </div>
    );
};
